import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

// Permission categories for better UX
const PERMISSION_CATEGORIES = {
  "User Management": ["users.view", "users.manage", "users.delete", "impersonate.users"],
  "Credits & Billing": ["credits.view", "credits.manage", "billing.view", "billing.manage"],
  "Content": ["contracts.view", "qa.view"],
  "Analytics & Audit": ["analytics.view", "audit.view"],
  "Settings & Support": ["settings.view", "settings.manage", "support.view", "support.manage"],
  "Promotions": ["promotions.view", "promotions.manage"],
};

const PERMISSION_LABELS = {
  "users.view": "View Users",
  "users.manage": "Manage Users",
  "users.delete": "Delete Users",
  "impersonate.users": "Impersonate Users",
  "credits.view": "View Credits",
  "credits.manage": "Manage Credits",
  "billing.view": "View Billing",
  "billing.manage": "Manage Billing",
  "contracts.view": "View Contracts",
  "qa.view": "View Q&A",
  "analytics.view": "View Analytics",
  "audit.view": "View Audit Log",
  "settings.view": "View Settings",
  "settings.manage": "Manage Settings",
  "support.view": "View Support Tools",
  "support.manage": "Use Support Tools",
  "promotions.view": "View Promotions",
  "promotions.manage": "Manage Promotions",
};

export default function AdminTeam() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [defaultPermissions, setDefaultPermissions] = useState({});

  // Form states
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newRole, setNewRole] = useState("admin");
  const [updateRole, setUpdateRole] = useState("admin");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/team/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAdmins(response.data.admins || []);
      setAvailablePermissions(response.data.available_permissions || []);
      setDefaultPermissions(response.data.default_permissions || {});
    } catch (error) {
      toast.error("Failed to load admin team");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    if (!newEmail || !newPassword) {
      toast.error("Please fill all fields");
      return;
    }

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/team/create`,
        { email: newEmail, password: newPassword, role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Admin created: ${newEmail}`);
      setShowCreateModal(false);
      setNewEmail("");
      setNewPassword("");
      setNewRole("admin");
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create admin");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/team/${selectedAdmin.id}/role`,
        { role: updateRole },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Role updated to ${updateRole}`);
      setShowRoleModal(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdatePermissions = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.put(
        `${API}/admin/team/${selectedAdmin.id}/permissions`,
        { permissions: selectedPermissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Permissions updated");
      setShowPermissionsModal(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update permissions");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteAdmin = async () => {
    const token = localStorage.getItem("admin_token");
    setActionLoading(true);
    try {
      await axios.delete(`${API}/admin/team/${selectedAdmin.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Admin deleted");
      setShowDeleteModal(false);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete admin");
    } finally {
      setActionLoading(false);
    }
  };

  const togglePermission = (perm) => {
    setSelectedPermissions(prev =>
      prev.includes(perm)
        ? prev.filter(p => p !== perm)
        : [...prev, perm]
    );
  };

  const getRoleBadge = (role) => {
    const colors = {
      owner_admin: "bg-purple-600",
      admin: "bg-blue-600",
      support_agent: "bg-green-600",
    };
    return colors[role] || "bg-gray-600";
  };

  const getRoleLabel = (role) => {
    return role.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground">Admin Team</h1>
          <p className="text-muted-foreground mt-1">Manage admin users and permissions</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          data-testid="add-admin-btn"
          className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded-md transition"
        >
          + Add Admin
        </button>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Admin Roles & Permissions:</p>
            <ul className="space-y-1 ml-4 list-disc">
              <li><strong>Owner Admin:</strong> Full access to everything including team management and user impersonation</li>
              <li><strong>Admin:</strong> Manage users and credits, view analytics and billing</li>
              <li><strong>Support Agent:</strong> Read-only access for customer support</li>
            </ul>
            <p className="mt-2 text-blue-700">💡 Permissions can be customized per admin user.</p>
          </div>
        </div>
      </div>

      {/* Admin Team Table */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Permissions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Last Login</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {admins.map((adminUser) => {
                  const currentUser = localStorage.getItem("admin_user");
                  const isCurrentUser = currentUser && JSON.parse(currentUser).id === adminUser.id;
                  const permCount = adminUser.permissions?.length || 0;

                  return (
                    <tr key={adminUser.id} className="hover:bg-muted/50 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <p className="text-foreground font-medium">{adminUser.email}</p>
                          {isCurrentUser && (
                            <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">You</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 ${getRoleBadge(adminUser.role)} text-white text-xs rounded-full`}>
                          {getRoleLabel(adminUser.role)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-muted-foreground text-sm">
                          {adminUser.role === "owner_admin" ? (
                            <span className="text-purple-400">All permissions</span>
                          ) : (
                            `${permCount} permissions`
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-muted-foreground text-sm">
                        {new Date(adminUser.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-muted-foreground text-sm">
                        {adminUser.last_login ? new Date(adminUser.last_login).toLocaleDateString() : "Never"}
                      </td>
                      <td className="px-6 py-4">
                        {!isCurrentUser && (
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setSelectedAdmin(adminUser);
                                setUpdateRole(adminUser.role);
                                setShowRoleModal(true);
                              }}
                              className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition"
                            >
                              Role
                            </button>
                            {adminUser.role !== "owner_admin" && (
                              <button
                                onClick={() => {
                                  setSelectedAdmin(adminUser);
                                  setSelectedPermissions(adminUser.permissions || []);
                                  setShowPermissionsModal(true);
                                }}
                                data-testid={`edit-permissions-${adminUser.id}`}
                                className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white text-xs rounded transition"
                              >
                                Permissions
                              </button>
                            )}
                            <button
                              onClick={() => {
                                setSelectedAdmin(adminUser);
                                setShowDeleteModal(true);
                              }}
                              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded transition"
                            >
                              Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-heading font-bold text-foreground">Add New Admin</h3>
            <input
              type="email"
              placeholder="Email address"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              data-testid="new-admin-email"
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <input
              type="password"
              placeholder="Password (min 8 characters)"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-testid="new-admin-password"
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              data-testid="new-admin-role"
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="support_agent">Support Agent (Read-only)</option>
              <option value="admin">Admin (User & credit management)</option>
              <option value="owner_admin">Owner Admin (Full access)</option>
            </select>
            <div className="flex gap-3">
              <button
                onClick={handleCreateAdmin}
                disabled={actionLoading}
                data-testid="confirm-create-admin"
                className="flex-1 px-4 py-2 bg-primary hover:bg-primary/90 disabled:opacity-50 text-primary-foreground rounded-md transition"
              >
                {actionLoading ? "Creating..." : "Create Admin"}
              </button>
              <button
                onClick={() => setShowCreateModal(false)}
                className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Update Role Modal */}
      {showRoleModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-md w-full p-6 space-y-4">
            <h3 className="text-xl font-heading font-bold text-foreground">Change Role</h3>
            <p className="text-muted-foreground text-sm">
              Changing role for: <strong>{selectedAdmin.email}</strong>
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-amber-800 text-sm">
                ⚠️ Changing role will reset permissions to the default for the new role.
              </p>
            </div>
            <select
              value={updateRole}
              onChange={(e) => setUpdateRole(e.target.value)}
              className="w-full px-4 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="support_agent">Support Agent (Read-only)</option>
              <option value="admin">Admin (User & credit management)</option>
              <option value="owner_admin">Owner Admin (Full access)</option>
            </select>
            <div className="flex gap-3">
              <button
                onClick={handleUpdateRole}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-md transition"
              >
                {actionLoading ? "Updating..." : "Update Role"}
              </button>
              <button
                onClick={() => setShowRoleModal(false)}
                className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Permissions Modal */}
      {showPermissionsModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 space-y-4">
            <div className="sticky top-0 bg-card pb-4 border-b border-border">
              <h3 className="text-xl font-heading font-bold text-foreground">Edit Permissions</h3>
              <p className="text-muted-foreground text-sm">
                Customizing permissions for: <strong>{selectedAdmin.email}</strong>
              </p>
            </div>
            
            <div className="space-y-6">
              {Object.entries(PERMISSION_CATEGORIES).map(([category, perms]) => (
                <div key={category} className="space-y-2">
                  <h4 className="text-sm font-semibold text-foreground border-b border-border pb-1">
                    {category}
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {perms.map(perm => (
                      <label
                        key={perm}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition ${
                          selectedPermissions.includes(perm) 
                            ? "bg-primary/10 border border-primary/30" 
                            : "bg-muted/50 border border-transparent hover:bg-muted"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedPermissions.includes(perm)}
                          onChange={() => togglePermission(perm)}
                          className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-foreground">
                          {PERMISSION_LABELS[perm] || perm}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="sticky bottom-0 bg-card pt-4 border-t border-border flex gap-3">
              <button
                onClick={handleUpdatePermissions}
                disabled={actionLoading}
                data-testid="save-permissions-btn"
                className="flex-1 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md transition"
              >
                {actionLoading ? "Saving..." : `Save (${selectedPermissions.length} selected)`}
              </button>
              <button
                onClick={() => {
                  setShowPermissionsModal(false);
                  setSelectedPermissions([]);
                }}
                className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 text-foreground rounded-md transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Admin Modal */}
      {showDeleteModal && selectedAdmin && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-red-200 rounded-lg max-w-md w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 border border-red-200 rounded-lg flex items-center justify-center">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-heading font-bold text-foreground">Delete Admin?</h3>
            </div>
            
            <div className="space-y-3">
              <p className="text-foreground">
                Are you sure you want to delete <strong>{selectedAdmin.email}</strong>?
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-red-800 text-sm font-medium">⚠️ This action cannot be undone!</p>
                <p className="text-red-700 text-xs mt-1">
                  This admin will lose all access to the admin portal.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleDeleteAdmin}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white rounded-md transition font-medium"
              >
                {actionLoading ? "Deleting..." : "Yes, Delete Admin"}
              </button>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={actionLoading}
                className="flex-1 px-4 py-2 bg-muted hover:bg-muted/80 disabled:opacity-50 text-foreground rounded-md transition"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
